import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  getInstallMethod,
  getInstallPlatform,
  isIosSafari,
  isStandalone,
} from '../browser';
import type {
  BeforeInstallPromptEvent,
  InstallInvocationResult,
  UsePwaInstallOptions,
  UsePwaInstallResult,
} from '../types';

function resolveInstallState() {
  return {
    isInstalled: isStandalone(),
    platform: getInstallPlatform(),
  };
}

export function usePwaInstall(
  options: UsePwaInstallOptions = {},
): UsePwaInstallResult {
  const analytics = options.analytics;
  const [installState, setInstallState] = useState(resolveInstallState);
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(display-mode: standalone)');

    const syncState = () => {
      setInstallState(resolveInstallState());
    };

    const onBeforeInstallPrompt = (event: Event) => {
      const promptEvent = event as BeforeInstallPromptEvent;
      promptEvent.preventDefault();
      setDeferredPrompt(promptEvent);
      analytics?.onInstallAvailable?.(getInstallPlatform());
      syncState();
    };

    const onInstalled = () => {
      setDeferredPrompt(null);
      setInstallState({
        isInstalled: true,
        platform: 'standalone',
      });
      analytics?.onInstalled?.(getInstallPlatform());
    };

    syncState();

    if ('addEventListener' in mediaQuery) {
      mediaQuery.addEventListener('change', syncState);
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      if ('removeEventListener' in mediaQuery) {
        mediaQuery.removeEventListener('change', syncState);
      }

      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [analytics]);

  const install = useCallback(async (): Promise<InstallInvocationResult> => {
    const platform = getInstallPlatform();

    if (isStandalone()) {
      return {
        platform,
        status: 'unavailable',
      };
    }

    if (isIosSafari()) {
      analytics?.onManualInstallOpened?.(platform);
      return {
        platform,
        status: 'manual',
      };
    }

    if (!deferredPrompt) {
      return {
        platform,
        status: 'unavailable',
      };
    }

    setDeferredPrompt(null);
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;

    if (choice.outcome === 'accepted') {
      setInstallState({
        isInstalled: true,
        platform: 'standalone',
      });
      analytics?.onInstallAccepted?.(platform);
      return {
        platform,
        status: 'accepted',
      };
    }

    analytics?.onInstallDismissed?.(platform);
    setInstallState(resolveInstallState());

    return {
      platform,
      status: 'dismissed',
    };
  }, [analytics, deferredPrompt]);

  const requiresManualInstall = !installState.isInstalled && isIosSafari();
  const promptAvailable = Boolean(deferredPrompt);
  const canInstall =
    !installState.isInstalled && (requiresManualInstall || promptAvailable);
  const installMethod = useMemo(() => {
    if (installState.isInstalled) {
      return 'unsupported';
    }

    return getInstallMethod();
  }, [installState.isInstalled]);

  return {
    canInstall,
    install,
    installMethod,
    isInstalled: installState.isInstalled,
    platform: installState.platform,
    promptAvailable,
    requiresManualInstall,
  };
}
